import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { deleteProduct, fetchProducts } from '../../api/productsApi';
import { placeholderSvgDataUrl } from '../../utils/placeholderImage';

const AdminProducts = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      const data = await fetchProducts(params);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.message || 'Failed to load products');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => String(p.title || '').toLowerCase().includes(q));
  }, [items, query]);

  const onDelete = async (p) => {
    if (!window.confirm(`Delete product "${p.title}"?`)) return;
    try {
      await deleteProduct(p.id);
      await load();
    } catch (e) { window.alert(e?.message || 'Delete failed'); }
  };

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes cardIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }

        .admin-prod-filters { background: #0F1B2D; border-radius: 12px; border: 1px solid rgba(255,255,255,0.05); padding: 16px 20px; margin-bottom: 20px; display: flex; gap: 12px; flex-wrap: wrap; align-items: center; justify-content: space-between; }
        .admin-prod-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
        .admin-prod-card { background: #0F1B2D; border-radius: 14px; border: 1px solid rgba(255,255,255,0.05); overflow: hidden; transition: all 0.3s ease; animation: cardIn 0.4s ease forwards; }
        .admin-prod-card:hover { border-color: rgba(255,215,0,0.25); transform: translateY(-3px); box-shadow: 0 10px 30px rgba(0,0,0,0.4); }

        @media (max-width: 768px) {
          .admin-prod-heading { display: none !important; }
          .admin-prod-add-btn { display: none !important; }
          .admin-prod-filters { flex-direction: column; padding: 12px; gap: 10px; border-radius: 14px; }
          .admin-prod-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .admin-prod-card { border-radius: 12px; }
          .admin-prod-card-img { height: 120px !important; }
          .admin-prod-card-body { padding: 10px !important; }
          .admin-prod-card-body > div:first-child { font-size: 12px !important; }
          .admin-prod-card a, .admin-prod-card button { padding: 6px 8px !important; font-size: 11px !important; }
        }
        @media (max-width: 480px) {
          .admin-prod-card-img { height: 100px !important; }
          .admin-prod-card-body { padding: 8px !important; }
          .admin-prod-card-body > div:first-child { font-size: 11px !important; }
        }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '12px', width: '100%' }}>
        <div className="admin-prod-heading" style={{ overflow: 'hidden' }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#F9FAFB', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Products</h1>
          <p style={{ margin: '4px 0 0', color: '#9CA3AF', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Manage your product catalog</p>
        </div>
        <button className="admin-prod-add-btn"
          onClick={() => navigate('/admin/products/new')}
          style={{
            padding: '8px 14px', borderRadius: '8px', border: 'none',
            background: 'linear-gradient(135deg, #FFD700, #FFA500)',
            color: '#0B1020', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s',
            whiteSpace: 'nowrap', justifyContent: 'center', flexShrink: 0
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add
        </button>
      </div>

      {/* Filters */}
      <div className="admin-prod-filters">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
          <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: '100%' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title..."
              style={{
                width: '100%', padding: '10px 12px 10px 36px', borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
                color: '#F3F4F6', fontSize: '14px', outline: 'none', boxSizing: 'border-box'
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(255,215,0,0.4)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{
              padding: '10px 36px 10px 14px', borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
              color: '#F3F4F6', fontSize: '14px', outline: 'none', cursor: 'pointer',
              appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <button
          onClick={load} disabled={loading}
          style={{
            padding: '10px 20px', borderRadius: '10px', border: '1px solid rgba(255,215,0,0.2)',
            background: 'transparent', color: '#FFD700', cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '13px', fontWeight: 500, opacity: loading ? 0.5 : 1, whiteSpace: 'nowrap',
            transition: 'all 0.2s', width: '100%'
          }}
        >
          Refresh
        </button>
      </div>

      {err && (
        <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.1)', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.2)', color: '#FCA5A5', fontSize: '14px', marginBottom: '20px' }}>
          {err}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Loading...</div>
      ) : (
        <>
          {filtered.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>No products found.</div>
          ) : (
            <div className="admin-prod-grid">
              {filtered.map((p, idx) => {
                const img = (p.images && p.images[0]) || placeholderSvgDataUrl({ width: 600, height: 360, text: p.title || 'Product' });
                return (
                  <div key={p.id} className="admin-prod-card" style={{ animationDelay: `${idx * 0.05}s` }}>
                    <div className="admin-prod-card-img" style={{ height: '180px', overflow: 'hidden', position: 'relative' }}>
                      <img src={img} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
                      {p.featured && (
                        <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#000', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                          Featured
                        </div>
                      )}
                      <span style={{
                        position: 'absolute', top: '10px', left: '10px',
                        padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                        background: p.status === 'active' ? 'rgba(52,211,153,0.15)' : 'rgba(239,68,68,0.15)',
                        color: p.status === 'active' ? '#34D399' : '#F87171',
                        backdropFilter: 'blur(4px)'
                      }}>
                        {p.status}
                      </span>
                    </div>
                    <div className="admin-prod-card-body" style={{ padding: '16px' }}>
                      <div style={{ fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '15px', marginBottom: '4px' }}>{p.title}</div>
                      <div style={{ color: '#FFD700', fontWeight: 700, fontSize: '16px', marginBottom: '4px' }}>
                        ₹{Number(p.price || 0).toLocaleString()}
                      </div>
                      <div style={{ color: '#6B7280', fontSize: '12px', marginBottom: '10px' }}>
                        {p.watt && `${p.watt} • `}{p.category || 'Uncategorized'}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Link to={`/admin/products/${p.id}`} style={{
                          padding: '8px 16px', borderRadius: '8px', border: 'none',
                          background: 'rgba(255,215,0,0.12)', color: '#FFD700',
                          cursor: 'pointer', fontWeight: 600, fontSize: '12px',
                          textDecoration: 'none', textAlign: 'center', flex: 1
                        }}>Edit</Link>
                        <Link to={`/product/${p.id}`} style={{
                          padding: '8px 16px', borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.1)', background: 'transparent',
                          color: '#9CA3AF', cursor: 'pointer', fontWeight: 600,
                          fontSize: '12px', textDecoration: 'none', textAlign: 'center', flex: 1
                        }}>View</Link>
        <button className="admin-prod-add-btn"
                          onClick={() => onDelete(p)}
                          style={{
                            padding: '8px 16px', borderRadius: '8px', border: 'none',
                            background: 'rgba(239,68,68,0.1)', color: '#F87171',
                            cursor: 'pointer', fontWeight: 600, fontSize: '12px', flex: 1
                          }}
                        >Delete</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminProducts;
